import type {
  ChainMetadata,
  Token,
  TokenKeyWithMetadata,
} from '@polaris/core/models'
import { useFieldValue, useForm } from 'form-atoms'
import { useQueryStates } from 'nuqs'
import { useEffect, useMemo } from 'react'
import { useSupportedChainsMap } from '@/src/queries/hooks/chains'
import {
  useSupportedTokens,
  useTokenAcrossChains,
} from '@/src/queries/hooks/tokens'
import {
  fromSearchParamsParsers,
  toSearchParamsParsers,
} from '@/src/stores/params'
import type { UseTokenFieldActionsProps } from '@/src/stores/swap/actions'
import { swapFormAtom } from '@/src/stores/swap/atoms'

export const useSelectedTickerInfo = () => {
  // 1. Get the ticker from query params instead of pathname
  const [queryParams, setSearchParameters] = useQueryStates({
    ...fromSearchParamsParsers,
    ...toSearchParamsParsers,
    ticker: { parse: (value) => value, serialize: (value) => value },
  })

  const {
    ticker: selectedTicker,
    toToken: toDenom,
    toChain,
    fromToken: fromDenom,
    fromChain,
  } = queryParams

  // Read FROM token from form state (set by SwapFormHydration with default USDC Osmosis)
  const { fieldAtoms } = useForm(swapFormAtom)
  const fromFieldValue = useFieldValue(fieldAtoms.from)

  const { data: searchResult, isLoading: areTokensLoading } =
    useSupportedTokens({
      query: selectedTicker ?? undefined,
      withPrice: false,
    })
  const searchToken = searchResult?.pages[0].assets[0]?.denoms_by_chain[0]
  const needFallback = !((fromDenom && fromChain) || (toDenom && toChain))

  // 3. Get the supported chains
  const { data: supportedChains, isLoading: areChainsLoading } =
    useSupportedChainsMap()

  // 4. Get ticker for from and to tokens
  const { data: fromAcrossChains, isLoading: fromIsLoading } =
    useTokenAcrossChains(fromChain, fromDenom)
  const fromToken = fromAcrossChains?.requested_asset
  const { data: toAcrossChains, isLoading: toIsLoading } = useTokenAcrossChains(
    toChain ?? (needFallback ? searchToken?.chain_id : undefined),
    toDenom ?? (needFallback ? searchToken?.denom : undefined),
  )
  const toToken = toAcrossChains?.requested_asset

  // Necessary to sync query states with search results from ticker search
  // and sync FROM token to URL if it's loaded but not in URL params
  useEffect(() => {
    const updates: Record<string, string | undefined> = {}

    // Sync TO token (fallback from search)
    if (
      needFallback &&
      !areTokensLoading &&
      searchToken &&
      searchToken.denom &&
      searchToken.chain_id
    ) {
      updates.toToken = searchToken.denom
      updates.toChain = searchToken.chain_id
    }

    // Sync FROM token from form state to URL if not in URL params
    // This ensures the default FROM token (USDC Osmosis) is reflected in the URL
    if (
      !fromDenom &&
      !fromChain &&
      fromFieldValue?.chain &&
      fromFieldValue?.token
    ) {
      updates.fromToken = fromFieldValue.token
      updates.fromChain = fromFieldValue.chain
    }

    // Only update if we have changes
    if (Object.keys(updates).length > 0) {
      setSearchParameters(updates)
    }
  }, [
    needFallback,
    areTokensLoading,
    searchToken,
    fromDenom,
    fromChain,
    fromFieldValue,
    setSearchParameters,
  ])

  // 5. Determine the active token
  let activeToken: UseTokenFieldActionsProps['direction'] = 'to'
  if (fromToken && fromToken.ticker === selectedTicker) activeToken = 'from'

  let primaryToken: Token | undefined = toToken
  let secondaryToken: Token | undefined = fromToken
  let tokenAcrossChainsData = toAcrossChains

  if (activeToken === 'from') {
    primaryToken = fromToken
    secondaryToken = toToken
    tokenAcrossChainsData = fromAcrossChains
  }

  // 6. Get the main denom from the token data
  const denom = primaryToken?.denom ?? searchToken?.denom!

  // 7. Get the main chain from the token data
  const chain = primaryToken?.chain_id ?? searchToken?.chain_id!

  const chains = useMemo(() => {
    const chains: (ChainMetadata & TokenKeyWithMetadata)[] = []

    const defaultChainId = tokenAcrossChainsData?.requested_asset.chain_id
    for (const tokenKey of tokenAcrossChainsData?.asset_across_chains ?? []) {
      const chain = supportedChains?.get(tokenKey.chain_id)
      if (!chain) continue

      // If the chain id is the same as the default chain id, add it to the beginning of the array
      // so it is the default in the dropdown (and is generally the main chain)
      // Also alleviates this issue:
      // https://linear.app/osmosis/issue/POL-3039/fix-nondeterministic-ordering-in-asset-across-chains-endpoint
      if (tokenKey.chain_id === defaultChainId) {
        chains.unshift({ ...chain, ...tokenKey })
      } else {
        chains.push({ ...chain, ...tokenKey })
      }
    }

    return chains
  }, [supportedChains, tokenAcrossChainsData])

  return {
    ticker: selectedTicker,
    chain,
    denom,
    activeToken,
    primaryToken,
    secondaryToken,
    chains,
    isLoading:
      areChainsLoading || fromIsLoading || toIsLoading || areTokensLoading,
  }
}
